// File: my-spa/src/components/ManageClients.tsx
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Modal from './Modal';
import ClientForm from './ClientForm';
import ClientAppointmentsHistory from './ClientAppointmentsHistory';
import { AuthContext } from '../auth/authContext';
import { PERMISSIONS } from '../auth/permissions';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';


enum Gender {
  MASCULINO = 'Masculino',
  FEMENINO = 'Femenino',
  OTRO = 'Otro',
  PREFIERO_NO_DECIR = 'Prefiero no decir',
}

interface Client {
  id: number;
  name: string;
  phone?: string;
  email?: string;
  age?: number;
  gender?: Gender;
  observations?: string;
  dni?: string; // <-- NUEVO CAMPO
  dateOfBirth?: string; // <-- NUEVO CAMPO
}

const ManageClients = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<number | null>(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState<boolean>(false);
  const [editingClient, setEditingClient] = useState<Client | undefined>(undefined);

  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState<boolean>(false);
  const [selectedClientForHistory, setSelectedClientForHistory] = useState<Client | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  // --- Paginación ---
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  const { hasPermission } = useContext(AuthContext);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get<Client[]>(`${API_BASE_URL}/clients`);
      setClients(response.data);
    } catch (err: any) {
      console.error("Error fetching clients:", err);
      setError(err.message || "Error al cargar los clientes.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClient = async (clientId: number, clientName: string) => {
    const confirmDelete = window.confirm(
      `¿Estás seguro de que quieres eliminar a "${clientName}"?\n\nEsta acción no se puede deshacer.`
    );

    if (!confirmDelete) {
      return;
    }

    setIsDeleting(clientId);
    setError(null);

    try {
      await axios.delete(`${API_BASE_URL}/clients/${clientId}`);
      setClients(prevClients => 
        prevClients.filter(client => client.id !== clientId)
      );
      console.log(`Cliente "${clientName}" eliminado exitosamente.`);
    } catch (err: any) {
      console.error("Error deleting client:", err);
      if (err.response?.status === 401) {
        setError("No tienes autorización para eliminar clientes. Por favor, inicia sesión nuevamente.");
      } else if (err.response?.status === 404) {
        setError("El cliente no fue encontrado. Puede que ya haya sido eliminado.");
        setClients(prevClients => 
          prevClients.filter(client => client.id !== clientId)
        );
      } else if (err.response?.status === 400) { // <-- ¡AÑADE ESTE MANEJO DE ERROR!
        setError(err.response?.data?.message || "No se puede eliminar el cliente.");
      } else {
        setError(err.message || "Error al eliminar el cliente.");
      }
    } finally {
      setIsDeleting(null);
    }
  };

  const handleOpenCreateModal = () => {
    setEditingClient(undefined);
    setIsFormModalOpen(true);
  };

  const handleOpenEditModal = (client: Client) => {
    setEditingClient(client);
    setIsFormModalOpen(true);
  };

  const handleFormSuccess = () => {
    setIsFormModalOpen(false);
    setEditingClient(undefined);
    fetchClients(); // Recarga la lista
  };

  const handleFormCancel = () => {
    setIsFormModalOpen(false);
    setEditingClient(undefined);
  };

  const handleOpenHistoryModal = (client: Client) => {
    setSelectedClientForHistory(client);
    setIsHistoryModalOpen(true);
  };

  const handleCloseHistoryModal = () => {
    setIsHistoryModalOpen(false);
    setSelectedClientForHistory(null);
  };

  // CÓDIGO ACTUALIZADO
  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.dni?.toLowerCase().includes(searchTerm.toLowerCase()) || // <-- Añadido
    client.phone?.includes(searchTerm) ||
    client.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.max(1, Math.ceil(filteredClients.length / itemsPerPage));
  const paginatedClients = filteredClients.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Al buscar o cambiar el tamaño de página, volvemos a la primera página
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  // Si la página actual queda fuera de rango (p. ej. tras eliminar), la ajustamos
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  if (isLoading) {
    return (
      <div>
        <p>Cargando clientes...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <p style={{ color: 'red' }}>Error: {error}</p>
        <button onClick={() => window.location.reload()}>Recargar Página</button>
      </div>
    );
  }

  return (
    <div className="admin-content-container">
      <h2>Gestionar Clientes</h2>

      {/* --- BUSCADOR Y BOTÓN CONDICIONAL --- */}
      <div className="table-toolbar">
        <input
          type="text"
          className="form-input table-search"
          placeholder="Buscar por nombre, dni, teléfono o email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {hasPermission(PERMISSIONS.CLIENTS_CREATE) && (
          <button
            className="action-button"
            style={{ backgroundColor: 'var(--color-success)' }}
            onClick={handleOpenCreateModal}
          >
            Añadir Nuevo Cliente
          </button>
        )}
      </div>

      {filteredClients.length === 0 ? (
        <p>No hay clientes para mostrar.</p>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nombre</th>
                  <th>DNI</th>
                  <th>Fecha de Nacimiento</th>
                  <th>Teléfono</th>
                  <th>Email</th>
                  <th>Edad</th>
                  <th>Género</th>
                  <th>Observaciones</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginatedClients.map((client) => (
                  <tr key={client.id}>
                    <td>{client.id}</td>
                    <td className="cell-strong">{client.name}</td>
                    <td>{client.dni || '-'}</td>
                    <td>{client.dateOfBirth ? new Date(client.dateOfBirth).toLocaleDateString('es-ES') : '-'}</td>
                    <td>{client.phone || '-'}</td>
                    <td>{client.email || '-'}</td>
                    <td>{client.age || '-'}</td>
                    <td>{client.gender || '-'}</td>
                    <td title={client.observations || ''}>
                      {client.observations ? `${client.observations.substring(0, 50)}...` : '-'}
                    </td>
                    <td>
                      <div className="table-actions">
                        {hasPermission(PERMISSIONS.CLIENTS_EDIT) && (
                          <button
                            className="action-button edit"
                            onClick={() => handleOpenEditModal(client)}
                          >
                            Editar
                          </button>
                        )}

                        {hasPermission(PERMISSIONS.CLIENTS_DELETE) && (
                          <button
                            className="action-button delete"
                            onClick={() => handleDeleteClient(client.id, client.name)}
                            disabled={isDeleting === client.id}
                          >
                            {isDeleting === client.id ? 'Eliminando...' : 'Eliminar'}
                          </button>
                        )}

                        <button
                          className="action-button invoice"
                          onClick={() => handleOpenHistoryModal(client)}
                        >
                          Historial
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* --- PAGINACIÓN --- */}
          <div className="pagination-controls">
            <button
              className="action-button"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Anterior
            </button>
            <span>
              Página {currentPage} de {totalPages} (Total: {filteredClients.length} clientes)
            </span>
            <button
              className="action-button"
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Siguiente
            </button>
            <select
              className="form-select"
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(parseInt(e.target.value, 10))}
            >
              <option value={5}>5 por página</option>
              <option value={10}>10 por página</option>
              <option value={20}>20 por página</option>
              <option value={50}>50 por página</option>
            </select>
          </div>
        </>
      )}

      <Modal
        isOpen={isFormModalOpen}
        onClose={handleFormCancel}
        title={editingClient ? 'Editar Cliente' : 'Añadir Nuevo Cliente'}
      >
        <ClientForm
          client={editingClient}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      </Modal>

      {selectedClientForHistory && (
        <Modal
          isOpen={isHistoryModalOpen}
          onClose={handleCloseHistoryModal}
          title={`Historial de Citas de ${selectedClientForHistory.name}`}
        >
          <ClientAppointmentsHistory
            clientId={selectedClientForHistory.id}
            clientName={selectedClientForHistory.name}
          />
        </Modal>
      )}
    </div>
  );
};

export default ManageClients;
